import React, { useState, useEffect } from 'react';
import Map from '../Layout/Map';

const DrugDetailsModal = ({ open, drug, onClose, onAddToCart }) => {
    const [distanceInfo, setDistanceInfo] = useState({ distance: '', duration: '' });

    // Reset distance info when drug changes
    useEffect(() => {
        setDistanceInfo({ distance: '', duration: '' });
    }, [drug]);

    // Callback to receive directions data from Map component
    const handleDirectionsReady = (data) => {
        if (data && !data.error && data.directions) {
            const leg = data.directions.routes[0]?.legs[0];
            if (leg) {
                setDistanceInfo({
                    distance: leg.distance.text,
                    duration: (leg.duration_in_traffic || leg.duration).text,
                });
            }
        }
    };

    if (!open || !drug) return null;

    const mediaUrl = process.env.REACT_APP_MEDIA_URL;
    const imageUrl = drug.productPicture
        ? `${mediaUrl}/${drug.productPicture}`
        : drug.logoPath
            ? `${mediaUrl}/${drug.logoPath}`
            : null;

    const categoryType = drug.categoryType || 'medication';

    // Get display name based on category
    const getItemName = () => {
        return drug.productName || drug.name || drug.itemName || 'N/A';
    };

    // Get price based on category
    const getPrice = () => {
        return drug.sellingPrice || drug.cost || 0;
    };

    const Info = ({ label, value }) => {
        if (!value || value === 'NILL' || value === 'nill' || value === 'Nil' || value === '.' || value === '0') return null;
        return (
            <div className="mb-4">
                <div className="inline-block bg-green-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-2">
                    {label}
                </div>
                <p className="text-gray-800 text-sm sm:text-base leading-relaxed">{value}</p>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 pt-24" style={{ zIndex: 1100 }}>
            <div className="bg-white rounded-xl w-full max-w-3xl max-h-[80vh] flex flex-col shadow-2xl">
                {/* Header - Fixed */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
                    <button onClick={onClose} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-100 flex items-center justify-center hover:bg-green-200 transition-colors">
                        <svg className="text-green-700" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                        </svg>
                    </button>
                    <h2 className="text-sm sm:text-base font-semibold text-gray-500">
                        {categoryType === 'medication' && 'Medication Details'}
                        {categoryType === 'lab' && 'Lab Test Details'}
                        {categoryType === 'equipment' && 'Equipment Details'}
                        {categoryType === 'bed' && 'Hospital Bed Details'}
                        {categoryType === 'service' && 'Service Details'}
                    </h2>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4">{/* Changed scrollbar style */}
                    <style>{`
            .overflow-y-auto::-webkit-scrollbar {
              width: 6px;
            }
            .overflow-y-auto::-webkit-scrollbar-track {
              background: #f1f1f1;
              border-radius: 10px;
            }
            .overflow-y-auto::-webkit-scrollbar-thumb {
              background: #888;
              border-radius: 10px;
            }
            .overflow-y-auto::-webkit-scrollbar-thumb:hover {
              background: #555;
            }
          `}</style>
                    {/* Title */}
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                        {getItemName()}
                    </h1>

                    {/* Product Image */}
                    {imageUrl && (
                        <div className="mb-6 flex justify-center bg-gray-50 rounded-xl p-4 sm:p-6">
                            <img
                                src={imageUrl}
                                alt={getItemName()}
                                className="w-full max-w-xs h-auto object-contain rounded-lg"
                            />
                        </div>
                    )}

                    {/* Details - Category Specific */}
                    <div className="space-y-4">
                        {/* Medication Fields */}
                        {categoryType === 'medication' && (
                            <>
                                <Info label="Ingredients" value={drug.ingredient} />
                                <Info label="Dosage form" value={drug.drugCategory} />
                                <Info label="Marketer" value={drug.manufacturer} />
                                <Info label="Application Purpose" value={drug.description} />
                                <Info
                                    label="Dosage"
                                    value={drug.dosage && drug.dosage !== 'NILL' && drug.dosage !== 'nill'
                                        ? `${drug.dosage} ${drug.measurementUnit || ''}`
                                        : null
                                    }
                                />
                                <Info
                                    label="Strength"
                                    value={drug.strength && drug.strength !== '0' && drug.strength !== 'NILL'
                                        ? `${drug.strength} ${drug.measurementUnit || ''}`
                                        : null
                                    }
                                />
                                <Info label="Best Before" value={drug.bestBefore} />
                                <Info label="Generic Name" value={drug.productGenericName} />
                                <Info label="Supplier" value={drug.supplier} />
                            </>
                        )}

                        {/* Lab Test Fields */}
                        {categoryType === 'lab' && (
                            <>
                                <Info label="Test Name" value={drug.name} />
                                <Info label="Description" value={drug.description} />
                                <Info label="Test Category" value={drug.category} />
                                <Info label="Sample Type" value={drug.sampleType} />
                                <Info label="Preparation Required" value={drug.preparation} />
                                <Info label="Turn Around Time" value={drug.turnaroundTime} />
                            </>
                        )}

                        {/* Equipment Fields */}
                        {categoryType === 'equipment' && (
                            <>
                                <Info label="Equipment Name" value={drug.name || drug.itemName} />
                                <Info label="Description" value={drug.description} />
                                <Info label="Model" value={drug.model} />
                                <Info label="Manufacturer" value={drug.manufacturer} />
                                <Info label="Condition" value={drug.condition} />
                                <Info label="Specifications" value={drug.specifications} />
                                <Info label="Warranty" value={drug.warranty} />
                            </>
                        )}

                        {/* Hospital Bed Fields */}
                        {categoryType === 'bed' && (
                            <>
                                <Info label="Bed Type" value={drug.name || drug.bedType} />
                                <Info label="Description" value={drug.description} />
                                <Info label="Ward" value={drug.ward} />
                                <Info label="Room Number" value={drug.roomNumber} />
                                <Info label="Bed Number" value={drug.bedNumber} />
                                <Info label="Availability" value={drug.availability} />
                                <Info label="Features" value={drug.features} />
                            </>
                        )}

                        {/* Service Fields */}
                        {categoryType === 'service' && (
                            <>
                                <Info label="Service Name" value={drug.itemName || drug.name} />
                                <Info label="Description" value={drug.description} />
                                <Info label="Category" value={drug.category} />
                                <Info label="Duration" value={drug.duration} />
                                <Info label="Requirements" value={drug.requirements} />
                                <Info label="Procedure" value={drug.procedure} />
                            </>
                        )}

                        {/* Price */}
                        <div className="pt-2">
                            <div className="inline-block bg-green-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-2">
                                {categoryType === 'bed' ? 'Daily Rate' : 'Price'}
                            </div>
                            <p className="text-3xl sm:text-4xl font-bold text-green-600">
                                ₦{getPrice()?.toLocaleString() || '0'}
                            </p>
                        </div>

                        {/* Location & Map Section */}
                        {drug.clinicLocation && drug.clinicLocation !== 'online' && (
                            <div className="pt-4 border-t border-gray-200">
                                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">Location & Directions</h3>
                                
                                {/* Distance and Duration Info */}
                                {distanceInfo.distance && (
                                    <div className="bg-green-50 rounded-lg p-4 mb-4 flex items-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <svg className="text-green-600" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                                <circle cx="12" cy="10" r="3"/>
                                            </svg>
                                            <div>
                                                <span className="text-xs text-gray-600">Distance</span>
                                                <p className="text-sm sm:text-base font-semibold text-gray-900">{distanceInfo.distance}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <svg className="text-green-600" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                <circle cx="12" cy="12" r="10"/>
                                                <polyline points="12 6 12 12 16 14" stroke="white" strokeWidth="2" fill="none"/>
                                            </svg>
                                            <div>
                                                <span className="text-xs text-gray-600">Travel Time</span>
                                                <p className="text-sm sm:text-base font-semibold text-gray-900">{distanceInfo.duration}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Map Component */}
                                <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200 mb-4">
                                    <Map 
                                        destinationAddress={drug.clinicLocation}
                                        onDirectionsReady={handleDirectionsReady}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Facility/Provider Information */}
                        {drug.clinicName && (
                            <div className="pt-4 border-t border-gray-200">
                                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">
                                    {categoryType === 'medication' && 'Pharmacy Information'}
                                    {categoryType === 'lab' && 'Laboratory Information'}
                                    {categoryType === 'equipment' && 'Supplier Information'}
                                    {categoryType === 'bed' && 'Hospital Information'}
                                    {categoryType === 'service' && 'Provider Information'}
                                </h3>
                                <div className="space-y-2 bg-gray-50 rounded-lg p-4">
                                    <div>
                                        <span className="text-xs sm:text-sm font-semibold text-gray-600">Name:</span>
                                        <p className="text-sm sm:text-base text-gray-900 font-medium">{drug.clinicName}</p>
                                    </div>
                                    {drug.clinicLocation && drug.clinicLocation !== 'online' && (
                                        <div>
                                            <span className="text-xs sm:text-sm font-semibold text-gray-600">Location:</span>
                                            <p className="text-sm sm:text-base text-gray-900">{drug.clinicLocation}</p>
                                        </div>
                                    )}
                                    {drug.phoneNumber && (
                                        <div>
                                            <span className="text-xs sm:text-sm font-semibold text-gray-600">Phone:</span>
                                            <p className="text-sm sm:text-base text-gray-900">{drug.phoneNumber}</p>
                                        </div>
                                    )}
                                    {drug.email && (
                                        <div>
                                            <span className="text-xs sm:text-sm font-semibold text-gray-600">Email:</span>
                                            <p className="text-sm sm:text-base text-gray-900">{drug.email}</p>
                                        </div>
                                    )}
                                    {drug.clinicContactPerson && (
                                        <div>
                                            <span className="text-xs sm:text-sm font-semibold text-gray-600">Contact Person:</span>
                                            <p className="text-sm sm:text-base text-gray-900">
                                                {drug.clinicContactPerson}
                                                {drug.clinicContactDesignation && ` (${drug.clinicContactDesignation})`}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer - Fixed */}
                <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex-shrink-0">
                    <button
                        onClick={() => {
                            onAddToCart();
                            onClose();
                        }}
                        disabled={true}
                        className="w-full bg-gray-300 text-gray-500 py-3 sm:py-3.5 rounded-xl font-bold text-base sm:text-lg cursor-not-allowed shadow-lg"
                    >
                        Add to Cart
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DrugDetailsModal;
